/**
 * Pure edits on free elements (flooring / counter / wall / object rects).
 * All clamp internally: sizes floor at MIN_FREE_ELEMENT_SIZE, and positions
 * stay inside the room plus its alcoves (`alcoveExtendedBounds`), the same
 * bounds the normalize backstop applies.
 */
import { clamp, geometryBounds, roundToHalfInch } from '../constraints';
import { freeElementTypeOf, hasObjectFields, retypesFloor } from '../freeElementTypes';
import { alcoveExtendedBounds, normalizeDegrees } from '../geometry';
import {
  type CornerName,
  type CustomAreaType,
  type FloorPlanFreeElement,
  type FloorPlanRoom,
  type ObjectSubtype,
  roomFreeElements,
  SQUARE_CORNER,
} from '../schemas';
import { chamferAfterEdit } from './chamferLegs';
import { makeFreeElement, MIN_FREE_ELEMENT_SIZE } from './constructors';
import { replaceAt, updateFreeElement } from './structural';

/** Typed {...r, ...patch} over the free-element union. */
function patched(r: FloorPlanFreeElement, patch: Partial<FloorPlanFreeElement>): FloorPlanFreeElement {
  return { ...r, ...patch } as FloorPlanFreeElement;
}

/** Append a free element (materializes a missing freeElements array). */
export function addFreeElement(room: FloorPlanRoom, el: FloorPlanFreeElement): FloorPlanRoom {
  return { ...room, freeElements: [...roomFreeElements(room), el] };
}

/** Remove the free element at `index`. */
export function removeFreeElement(room: FloorPlanRoom, index: number): FloorPlanRoom {
  const els = roomFreeElements(room);
  if (index < 0 || index >= els.length) return room;
  return { ...room, freeElements: els.filter((_, i) => i !== index) };
}

const DEFAULT_FLOOR_TYPE_NAMES = ['Tile', 'Hardwood', 'Carpet', 'Vinyl', 'Stone'];

/** First unused floor-type name for a new flooring region; all used ⇒ the first name; none allowed ⇒ unset. */
function nextFloorType(room: FloorPlanRoom, names: readonly string[]): string | undefined {
  const used = new Set(roomFreeElements(room).filter(retypesFloor).map(r => r.floorType));
  return names.find(n => !used.has(n)) ?? names[0];
}

/**
 * A sensible fresh element of the given kind, with its type's add defaults
 * (`freeElementTypes`): a centered square a third of the room's smaller
 * dimension unless the type sizes it; flooring gets the next unused floor
 * type, drawn from `floorTypeNames` when the host gives the allowed types,
 * and left unset when the host allows none; objects default to a small
 * furniture box.
 */
export function defaultFreeElement(room: FloorPlanRoom, kind: CustomAreaType, floorTypeNames?: readonly string[]): FloorPlanFreeElement {
  const type = freeElementTypeOf(kind);
  const side = roundToHalfInch(type.addedSize ?? Math.max(1, Math.min(room.width, room.length) / 3));
  const floorType = type.floorRole === 'retypes' ? nextFloorType(room, floorTypeNames ?? DEFAULT_FLOOR_TYPE_NAMES) : undefined;
  const element = makeFreeElement(kind, roundToHalfInch(room.width / 2 - side / 2), roundToHalfInch(room.length / 2 - side / 2), side, side, floorType);
  return { ...element, ...type.addedWith } as FloorPlanFreeElement;
}

/** How far a duplicate sits from its source (ft): 4 in right and 4 in down. */
export const DUPLICATE_OFFSET = 4 / 12;

/**
 * A complete copy of `element`, every property, offset by DUPLICATE_OFFSET
 * in x and y (clamped to the geometry bounds) so it doesn't sit exactly on
 * its source.
 */
export function duplicateFreeElement(room: FloorPlanRoom, element: FloorPlanFreeElement): FloorPlanFreeElement {
  const r = structuredClone(element);
  const gb = geometryBounds(room);
  r.x = clamp(roundToHalfInch(r.x + DUPLICATE_OFFSET), gb.minX, Math.max(gb.minX, gb.maxX - r.width));
  r.y = clamp(roundToHalfInch(r.y + DUPLICATE_OFFSET), gb.minY, Math.max(gb.minY, gb.maxY - r.length));
  return r;
}

/** Move a free element; it stays inside the interior bounds. */
export function setFreeElementPosition(room: FloorPlanRoom, index: number, pos: { x?: number; y?: number }): FloorPlanRoom {
  const b = alcoveExtendedBounds(room);
  return updateFreeElement(room, index, r => {
    const x = pos.x != null ? clamp(roundToHalfInch(pos.x), b.minX, b.maxX - r.width) : r.x;
    const y = pos.y != null ? clamp(roundToHalfInch(pos.y), b.minY, b.maxY - r.length) : r.y;
    return x === r.x && y === r.y ? r : patched(r, { x, y });
  });
}

export type FreeElementSide = 'left' | 'right' | 'top' | 'bottom';

/**
 * Slide a free element (size fixed) so the given side sits `gap` from the
 * matching interior bound, clamped like setFreeElementPosition.
 */
export function setFreeElementGap(room: FloorPlanRoom, index: number, side: FreeElementSide, gap: number): FloorPlanRoom {
  const r = roomFreeElements(room)[index];
  if (!r) return room;
  const b = alcoveExtendedBounds(room);
  return setFreeElementPosition(
    room,
    index,
    side === 'left'
      ? { x: b.minX + gap }
      : side === 'right'
      ? { x: b.maxX - gap - r.width }
      : side === 'top'
      ? { y: b.minY + gap }
      : { y: b.maxY - gap - r.length },
  );
}

/**
 * Resize a free element: floors at MIN_FREE_ELEMENT_SIZE, caps at the
 * interior span, and re-clamps the position so it stays inside.
 */
export function setFreeElementSize(room: FloorPlanRoom, index: number, size: { width?: number; length?: number }): FloorPlanRoom {
  const b = alcoveExtendedBounds(room);
  return updateFreeElement(room, index, r => {
    const width = size.width != null ? Math.min(Math.max(MIN_FREE_ELEMENT_SIZE, roundToHalfInch(size.width)), b.maxX - b.minX) : r.width;
    const length = size.length != null ? Math.min(Math.max(MIN_FREE_ELEMENT_SIZE, roundToHalfInch(size.length)), b.maxY - b.minY) : r.length;
    if (width === r.width && length === r.length) return r;
    return patched(r, {
      width,
      length,
      x: clamp(r.x, b.minX, b.maxX - width),
      y: clamp(r.y, b.minY, b.maxY - length),
    });
  });
}

/**
 * Set the raw frame (drag path: bounds were pre-clamped at drag start).
 * Sizes still floor at MIN_FREE_ELEMENT_SIZE.
 */
export function setFreeElementFrame(
  room: FloorPlanRoom,
  index: number,
  frame: { x: number; y: number; width: number; length: number },
): FloorPlanRoom {
  return updateFreeElement(room, index, r => {
    const x = roundToHalfInch(frame.x);
    const y = roundToHalfInch(frame.y);
    const width = Math.max(MIN_FREE_ELEMENT_SIZE, roundToHalfInch(frame.width));
    const length = Math.max(MIN_FREE_ELEMENT_SIZE, roundToHalfInch(frame.length));
    if (x === r.x && y === r.y && width === r.width && length === r.length) return r;
    return patched(r, { x, y, width, length });
  });
}

/** Set the element's 3D height (≥ 0). */
export function setFreeElementHeight(room: FloorPlanRoom, index: number, height: number): FloorPlanRoom {
  return updateFreeElement(room, index, r => {
    const h = Math.max(0, roundToHalfInch(height));
    return h === r.height ? r : patched(r, { height: h });
  });
}

/** Set the element's base (height off the floor, ≥ 0). */
export function setFreeElementBase(room: FloorPlanRoom, index: number, base: number): FloorPlanRoom {
  return updateFreeElement(room, index, r => {
    const b = Math.max(0, roundToHalfInch(base));
    return b === r.base ? r : patched(r, { base: b });
  });
}

/** Set the plan rotation, normalized to [0, 360) with 0 stored as undefined. */
export function setFreeElementRotation(room: FloorPlanRoom, index: number, degrees: number): FloorPlanRoom {
  return updateFreeElement(room, index, r => {
    const rot = normalizeDegrees(degrees) || undefined;
    return rot === r.rotation ? r : patched(r, { rotation: rot });
  });
}

/** Set the floor type: flooring elements only (the type that retypes the floor). */
export function setFreeElementFloorType(room: FloorPlanRoom, index: number, floorType: string): FloorPlanRoom {
  return updateFreeElement(room, index, r => !retypesFloor(r) || r.floorType === floorType ? r : patched(r, { floorType }));
}

/** Set object-only fields (subtype / name / notBuiltIn): object elements only. */
export function setFreeElementObjectProps(
  room: FloorPlanRoom,
  index: number,
  props: { subtype?: ObjectSubtype; name?: string; notBuiltIn?: boolean },
): FloorPlanRoom {
  return updateFreeElement(room, index, r => {
    if (!hasObjectFields(r)) return r;
    const next = { ...r };
    if (props.subtype != null) next.subtype = props.subtype;
    if (props.name != null) next.name = props.name.trim() || undefined;
    if (props.notBuiltIn != null) next.notBuiltIn = props.notBuiltIn;
    return next;
  });
}

/** Adjacent corners whose chamfer legs bound this corner's legs. */
const CORNER_NEIGHBORS: Record<CornerName, { h: CornerName; v: CornerName }> = {
  backLeftCorner: { h: 'backRightCorner', v: 'frontLeftCorner' },
  backRightCorner: { h: 'backLeftCorner', v: 'frontRightCorner' },
  frontRightCorner: { h: 'frontLeftCorner', v: 'backRightCorner' },
  frontLeftCorner: { h: 'frontRightCorner', v: 'backLeftCorner' },
};

/**
 * Set one or both legs of a free element's corner chamfer, clamped so the
 * chamfer can't cross its neighbor's chamfer on either side.
 */
export function setFreeElementCornerChamfer(
  room: FloorPlanRoom,
  index: number,
  which: CornerName,
  legs: { dx?: number; dy?: number },
): FloorPlanRoom {
  return updateFreeElement(room, index, element => {
    const neighbors = CORNER_NEIGHBORS[which];
    const current = element[which] ?? SQUARE_CORNER;
    const chamfer = chamferAfterEdit(current, legs, {
      dx: Math.max(0, element.width - (element[neighbors.h]?.dx ?? 0)),
      dy: Math.max(0, element.length - (element[neighbors.v]?.dy ?? 0)),
    });
    return chamfer === current ? element : patched(element, { [which]: chamfer });
  });
}

/** Replace a free element wholesale (used by gesture commits). */
export function replaceFreeElement(room: FloorPlanRoom, index: number, el: FloorPlanFreeElement): FloorPlanRoom {
  if (!room.freeElements?.[index]) return room;
  return { ...room, freeElements: replaceAt(room.freeElements, index, el) };
}
